const COLS = 16;
const ROWS = 12;

export function cellAtPoint(x, y, rect) {
  if (rect.width <= 0 || rect.height <= 0 || x < rect.left || y < rect.top ||
      x >= rect.left + rect.width || y >= rect.top + rect.height) return null;
  return Math.floor((y - rect.top) / rect.height * ROWS) * COLS +
    Math.floor((x - rect.left) / rect.width * COLS);
}

/** Fill gaps when the browser coalesces pointer movement into fewer events. */
export function cellsBetween(from, to) {
  if (from === null) return [to];
  let x = from % COLS, y = Math.floor(from / COLS);
  const endX = to % COLS, endY = Math.floor(to / COLS);
  const dx = Math.abs(endX - x), dy = -Math.abs(endY - y);
  const sx = x < endX ? 1 : -1, sy = y < endY ? 1 : -1;
  let error = dx + dy;
  const cells = [];
  for (;;) {
    cells.push(y * COLS + x);
    if (x === endX && y === endY) return cells;
    const twice = error * 2;
    if (twice >= dy) { error += dy; x += sx; }
    if (twice <= dx) { error += dx; y += sy; }
  }
}

/** Pointer capture routes touch movement to the editor; coordinates pick cells. */
export function attachSketchInput(root, paint, onFinish = () => {}) {
  const doc = root.ownerDocument;
  let pointer = null, grid = null, previous = null;

  function cancel() {
    const oldGrid = grid, oldPointer = pointer;
    pointer = null; grid = null; previous = null;
    try {
      if (oldPointer !== null && oldGrid?.hasPointerCapture?.(oldPointer)) oldGrid.releasePointerCapture(oldPointer);
    } catch { /* The editor may have been replaced by a new phase. */ }
  }
  function finish(event) {
    if (pointer === null || (event?.pointerId !== undefined && event.pointerId !== pointer)) return;
    cancel();
    onFinish();
  }
  function paintAt(event) {
    if (!grid?.isConnected) return finish();
    const index = cellAtPoint(event.clientX, event.clientY, grid.getBoundingClientRect());
    if (index === null) { previous = null; return; }
    for (const id of cellsBetween(previous, index)) {
      const cell = grid.querySelector(`[data-cell="${id}"]`);
      if (cell) paint(cell);
    }
    previous = index;
  }
  function focusCell(cell, editor) {
    const previousFocus = editor.querySelector('[tabindex="0"]');
    if (previousFocus) previousFocus.tabIndex = -1;
    cell.tabIndex = 0;
    cell.focus({ preventScroll: true });
  }

  root.addEventListener('pointerdown', (event) => {
    const cell = event.target.closest?.('[data-cell]');
    const editor = cell?.closest('#sketch-editor');
    if (!editor || pointer !== null || event.button !== 0 || event.isPrimary === false) return;
    pointer = event.pointerId;
    grid = editor;
    previous = null;
    focusCell(cell, editor);
    try { grid.setPointerCapture?.(pointer); } catch { /* Document release still ends the stroke. */ }
    paintAt(event);
    event.preventDefault();
  });
  root.addEventListener('pointermove', (event) => {
    if (event.pointerId !== pointer) return;
    paintAt(event);
    event.preventDefault();
  });
  doc.addEventListener('pointerup', finish);
  doc.addEventListener('pointercancel', finish);
  root.addEventListener('lostpointercapture', (event) => { if (event.target === grid) finish(event); });
  doc.defaultView?.addEventListener('blur', () => finish());

  // Native button activation covers Space, Enter and assistive-technology clicks.
  root.addEventListener('click', (event) => {
    const cell = event.target.closest?.('[data-cell]');
    if (event.detail === 0 && cell?.closest('#sketch-editor')) paint(cell);
  });
  root.addEventListener('keydown', (event) => {
    const cell = event.target.closest?.('[data-cell]');
    const editor = cell?.closest('#sketch-editor');
    if (!editor) return;
    const index = Number(cell.dataset.cell);
    const x = index % COLS, y = Math.floor(index / COLS);
    const next = {
      ArrowLeft: y * COLS + Math.max(0, x - 1),
      ArrowRight: y * COLS + Math.min(COLS - 1, x + 1),
      ArrowUp: Math.max(0, y - 1) * COLS + x,
      ArrowDown: Math.min(ROWS - 1, y + 1) * COLS + x,
      Home: y * COLS,
      End: y * COLS + COLS - 1,
    }[event.key];
    if (next === undefined) return;
    const target = editor.querySelector(`[data-cell="${next}"]`);
    if (target) focusCell(target, editor);
    event.preventDefault();
  });
  return { get active() { return pointer !== null; }, cancel };
}
